import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Phone, ChevronRight } from "lucide-react";
import Seo from "@/components/Seo";
import { PROJECTS } from "@/data/projects";
import { SITE } from "@/lib/site";

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);
  if (!project) return <Navigate to="/projects" replace />;

  const related = PROJECTS.filter((p) => p.slug !== slug).slice(0, 3);

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://rvasigns.com/" },
      { "@type": "ListItem", "position": 2, "name": "Projects", "item": "https://rvasigns.com/projects" },
      { "@type": "ListItem", "position": 3, "name": project.title, "item": `https://rvasigns.com/projects/${project.slug}` },
    ],
  };

  return (
    <div data-testid={`project-detail-${project.slug}`} className="bg-white">
      <Seo
        path={`/projects/${project.slug}`}
        title={`${project.title} — Project Example`}
        description={project.description}
        image={project.coverImage}
        jsonLd={breadcrumbJsonLd}
      />

      {/* BREADCRUMB */}
      <nav className="max-w-5xl mx-auto px-6 lg:px-8 pt-8 pb-2" aria-label="Breadcrumb">
        <ol className="flex items-center gap-2 text-xs text-[#1A1A1A]/60 flex-wrap">
          <li><Link to="/" className="hover:text-[#F97316] transition-colors font-semibold uppercase tracking-wider">Home</Link></li>
          <li className="text-[#1A1A1A]/30"><ChevronRight className="w-3 h-3" /></li>
          <li><Link to="/projects" className="hover:text-[#F97316] transition-colors font-semibold uppercase tracking-wider">Projects</Link></li>
          <li className="text-[#1A1A1A]/30"><ChevronRight className="w-3 h-3" /></li>
          <li className="text-[#1A1A1A]/80 font-semibold uppercase tracking-wider truncate max-w-[60vw]">{project.title}</li>
        </ol>
      </nav>

      {/* HERO */}
      <section className="pt-6 pb-10 lg:pt-10 lg:pb-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-0.5 bg-[#F97316]" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Project Example</span>
          </div>
          <h1 className="font-heading text-[#1A1A1A] tracking-tight leading-[0.98]" style={{ fontWeight: 900, fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
            {project.title}
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#2B2F33]/85 leading-relaxed">
            {project.description}
          </p>
        </div>
      </section>

      {/* PHOTO */}
      <section className="pb-10 lg:pb-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="aspect-[4/3] bg-[#F8F8F5] border border-[#1A1A1A]/8 overflow-hidden">
            {project.coverImage ? (
              <img src={project.coverImage} alt={project.title} className="w-full h-full object-cover object-center" />
            ) : (
              <div className="w-full h-full" />
            )}
          </div>
        </div>
      </section>

      {/* RELATED SERVICE */}
      {project.relatedServicePage && (
        <section className="pb-16 lg:pb-24 bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="font-heading text-[#1A1A1A] tracking-tight leading-[1.05]" style={{ fontWeight: 900, fontSize: "clamp(1.5rem, 3vw, 2rem)" }}>
                Related service
              </h2>
              <p className="mt-5 text-lg text-[#2B2F33]/85 leading-relaxed">
                <Link
                  to={project.relatedServicePage}
                  data-testid={`project-related-service-${project.slug}`}
                  className="text-[#F97316] font-semibold hover:underline"
                >
                  {project.relatedServiceLabel}
                </Link>
              </p>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-[#F8F8F5] border-t border-[#1A1A1A]/8">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A] leading-[1.05]" style={{ fontWeight: 900 }}>
            Have a similar project?
          </h2>
          <p className="mt-5 text-lg text-[#2B2F33]/80 max-w-xl mx-auto leading-relaxed">
            Send Todd a note or give him a call. He usually responds within one business day.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 transition-colors"
              data-testid="project-detail-cta"
            >
              Discuss Your Sign Project <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href={SITE.phoneHref}
              className="border-2 border-[#1A1A1A]/15 hover:border-[#1A1A1A] text-[#1A1A1A] text-sm font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 transition-colors"
            >
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
          </div>
        </div>
      </section>

      {/* MORE PROJECTS */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-0.5 bg-[#F97316]" />
                <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">More Projects</span>
              </div>
              <h2 className="font-heading text-3xl tracking-tight text-[#1A1A1A] leading-tight" style={{ fontWeight: 900 }}>
                Other project examples
              </h2>
            </div>
            <Link to="/projects" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#F97316] hover:gap-3 transition-all">
              <ArrowLeft className="w-4 h-4" /> All projects
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {related.map((p) => (
              <Link key={p.slug} to={`/projects/${p.slug}`} className="group block">
                <div className="aspect-[4/3] bg-[#F8F8F5] border border-[#1A1A1A]/8 overflow-hidden">
                  {p.coverImage && (
                    <img src={p.coverImage} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]" loading="lazy" />
                  )}
                </div>
                <h3 className="mt-4 font-heading text-lg text-[#1A1A1A] tracking-tight" style={{ fontWeight: 800 }}>{p.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
